import BaseParser from './BaseParser';
import { TOOL_TYPE } from './ToolType';

/**
 * PracticeProposalParser - `[PRACTICE_PROPOSAL:<uuid>]`, the tag the study tutor's
 * `propose_practice` tool leaves in the text (TANAI-2616).
 *
 * The tag is untrusted text: the same parser runs on the student's own messages and on whatever
 * the model writes. So only the exact shape of the contract (lowercase UUID, nothing else inside
 * the brackets) becomes a part, and any other `[PRACTICE_PROPOSAL…` fragment is removed from the
 * text instead of reaching the Markdown. Whether a valid part becomes a card (assistant message,
 * not read-only, not streaming) is decided by ChatMessage.
 */

const UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const TAG_PREFIX = '[PRACTICE_PROPOSAL';
const VALID_TAG = new RegExp(`\\[PRACTICE_PROPOSAL:(${UUID_PATTERN})\\]`, 'g');
const VALID_TAG_EXACT = new RegExp(`^\\[PRACTICE_PROPOSAL:${UUID_PATTERN}\\]$`);
// Any fragment that starts like the tag: up to its closing bracket, or to the end of the line when
// it never closes. The length cap keeps a stray prefix from swallowing a whole paragraph.
const ANY_TAG_LIKE = /\[PRACTICE_PROPOSAL[^\]\n]{0,64}\]?/g;

type PracticeProposalMatch = {
  index: number;
  length: number;
  proposalId: string;
  type: string;
};

export type PracticeProposalPart = {
  proposalId: string;
  type: string;
};

// A tag cut short at the end of the text (`…[PRACTICE_PRO`): mid-stream it is the part still to
// come, and in a finished message it can only be a truncated one. `[P` is the shortest tail worth
// hiding: a lone `[` is too common (Markdown links) to blank.
function removeTrailingPartialTag(text: string): string {
  const lastBracket = text.lastIndexOf('[');
  if (lastBracket === -1) return text;

  const tail = text.slice(lastBracket);
  const isPartialTag = tail.length >= 2 && TAG_PREFIX.startsWith(tail);

  return isPartialTag ? text.slice(0, lastBracket) : text;
}

function removeInvalidTags(content: string): string {
  const withoutInvalid = content.replace(ANY_TAG_LIKE, (fragment) => (VALID_TAG_EXACT.test(fragment) ? fragment : ''));
  return removeTrailingPartialTag(withoutInvalid);
}

/**
 * Text of a message that is still streaming: removes every tag, valid or not, and the tail of a tag
 * that has only partly arrived, so the student never sees it flicker. The card itself only appears
 * once the message is complete.
 */
export function stripPracticeProposalTags(text: string): string {
  if (!text) return text;
  return removeTrailingPartialTag(text.replace(ANY_TAG_LIKE, ''));
}

class PracticeProposalParser extends BaseParser {
  constructor() {
    super('PracticeProposal', 2);
  }

  getPatterns(): RegExp[] {
    return [VALID_TAG];
  }

  convertContent(content: string): string {
    return removeInvalidTags(content);
  }

  // The parameters of the next two overrides are optional only because BaseParser (JS) declares
  // these methods without any, and an override may not require more arguments than its base.
  extractMatches(content = ''): PracticeProposalMatch[] {
    const matches: PracticeProposalMatch[] = [];
    const pattern = new RegExp(VALID_TAG.source, 'g');
    let match = pattern.exec(content);

    while (match !== null) {
      matches.push({ index: match.index, length: match[0].length, proposalId: match[1], type: TOOL_TYPE.PRACTICE_PROPOSAL });
      match = pattern.exec(content);
    }

    return matches;
  }

  createPart(match?: PracticeProposalMatch): PracticeProposalPart {
    return { type: TOOL_TYPE.PRACTICE_PROPOSAL, proposalId: match?.proposalId ?? '' };
  }
}

export default PracticeProposalParser;
